import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Flame, CalendarDays, Users } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { WhoIsInCard, STATUS_META, ORDER, fmtPtoRange } from "@/components/WhoIsInCard";
import { CoverageBoard, PersonWeekDialog } from "@/components/PresenceHistory";
import { fmtShiftTime, untilText } from "@/components/PresenceChips";
import { Button } from "@/components/ui/button";

// Iter 143 — one page per team: members with live presence, who's on call,
// upcoming time off and the team's coverage strip. All data comes from the
// existing /inout payload, filtered to this team.
export default function TeamPage() {
  const { name } = useParams();
  const teamName = decodeURIComponent(name || "");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);

  const load = () => api.get("/inout").then((r) => setData(r.data)).catch((e) => {
    if (e.response?.status === 403) navigate("/dashboard");
  });
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); /* eslint-disable-line */ }, []);

  // Iter 148 — the team lead sorts first.
  const leadId = (data?.team_leads || {})[teamName] || "";
  const members = useMemo(() => (data?.staff || [])
    .filter((s) => (s.teams?.length ? s.teams : (s.team ? [s.team] : [])).includes(teamName))
    .sort((a, b) => (a.user_id === leadId ? -1 : b.user_id === leadId ? 1 : 0)
                    || (a.name || "").localeCompare(b.name || "")), [data, teamName, leadId]);
  const lead = members.find((m) => m.user_id === leadId);

  if (user?.base_role !== "staff") return null;

  const counts = ORDER.reduce((a, s) => { a[s] = members.filter((m) => m.status === s).length; return a; }, {});
  const onCall = members.filter((m) => m.on_call);
  const pto = members
    .flatMap((m) => (m.upcoming_pto || []).map((e) => ({ ...e, name: m.name, avatar_url: m.avatar_url })))
    .filter((e) => e.start)
    .sort((a, b) => a.start.localeCompare(b.start));

  return (
    <div className="space-y-5" data-testid="team-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/teams" className="mb-1 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-primary"
            data-testid="team-back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> All teams
          </Link>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{teamName}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {members.length} {members.length === 1 ? "person" : "people"}
            {lead ? <> · lead <span className="font-semibold text-slate-700" data-testid="team-lead-name">{lead.name}</span></> : null}
            {" · live presence, on call and coverage"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="team-summary">
          {ORDER.map((s) => (
            <span key={s} data-testid={`team-summary-${s}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_META[s].chip}`}>
              <span className={`h-2 w-2 rounded-full ${STATUS_META[s].dot}`} />
              {STATUS_META[s].label} <span className="font-bold">{counts[s] || 0}</span>
            </span>
          ))}
        </div>
      </div>

      {members.length === 0 ? (
        <div className="rounded-xl border border-border bg-card px-4 py-12 text-center text-sm text-muted-foreground" data-testid="team-empty">
          Nobody is on the {teamName} team yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" data-testid="team-grid">
          {members.map((row) => (
            <div key={row.user_id} className="relative">
              {row.user_id === leadId && (
                <span data-testid="team-lead-badge"
                  className="absolute -top-2 left-1/2 z-10 -translate-x-1/2 rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow">
                  Lead
                </span>
              )}
              <WhoIsInCard row={row} me={{ user_id: data?.me }} appId={data?.intercom_app_id}
                prefix="team" large onAvatarUpdated={load} onHistory={setHistoryRow} />
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4" data-testid="team-oncall">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
            <Flame className="h-4 w-4 text-orange-500" fill="currentColor" /> On call now
          </div>
          {onCall.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nobody from {teamName} is on call right now.</p>
          ) : (
            <ul className="space-y-2">
              {onCall.map((m) => (
                <li key={m.user_id} className="flex items-center gap-2 text-xs" data-testid={`team-oncall-${m.user_id}`}>
                  <Avatar name={m.name} src={m.avatar_url} size="sm" fallback="mystery" />
                  <span className="min-w-0 flex-1 truncate font-medium text-foreground">{m.name}</span>
                  <span className="shrink-0 text-muted-foreground">
                    {m.on_call_handover?.schedule || "incident.io"}
                    {m.on_call_handover?.ends_at ? ` · until ${fmtShiftTime(m.on_call_handover.ends_at)} (${untilText(m.on_call_handover.ends_at)})` : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-4" data-testid="team-pto">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
            <CalendarDays className="h-4 w-4 text-primary" /> Upcoming time off
          </div>
          {pto.length === 0 ? (
            <p className="text-xs text-muted-foreground">No upcoming PTO booked for this team.</p>
          ) : (
            <ul className="space-y-2">
              {pto.slice(0, 8).map((e, i) => (
                <li key={`${e.name}-${e.start}-${i}`} className="flex items-center gap-2 text-xs" data-testid={`team-pto-${i}`}>
                  <Avatar name={e.name} src={e.avatar_url} size="sm" fallback="mystery" />
                  <span className="min-w-0 flex-1 truncate font-medium text-foreground">{e.name}</span>
                  <span className="shrink-0 text-muted-foreground">
                    {fmtPtoRange(e.start, e.end)}{e.note ? ` · ${e.note}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <CoverageBoard team={teamName} startOpen />

      <PersonWeekDialog open={!!historyRow} onOpenChange={(o) => !o && setHistoryRow(null)}
        userId={historyRow?.user_id} name={historyRow?.name} />
    </div>
  );
}
